import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useExcelData } from "../../context/ExcelDataContext";
import { fetchCostModelAnalysisList, fetchLatestSpendCleaning } from "../../services/ExcelDataContext";
import RunIcon from "../components/icons/run";
import { toast } from "../lib/toast";
import { normalizeCostModelResult } from "../lib/normalizeCostModelResult";
import { useSimplAI } from "../../imports/useSimpleAI";

const getRowFieldValue = (row: Record<string, any>, fieldNames: string[]) => {
  const normalizedNames = new Set(
    fieldNames.map((fieldName) => fieldName.toLowerCase().replace(/[^a-z0-9]/g, ""))
  );
  const matchingKey = Object.keys(row || {}).find((key) =>
    normalizedNames.has(key.toLowerCase().replace(/[^a-z0-9]/g, ""))
  );

  return matchingKey ? String(row[matchingKey] ?? "").trim() : "";
};

type SavedModelRow = {
  id: string;
  product: string;
  cost: string;
  conf: string;
  ver: string;
  status: string;
  createdAt: string;
};

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  "Argentina": "ARS",
  "Australia": "AUD",
  "Brazil": "BRL",
  "Canada": "CAD",
  "China": "CNY",
  "France": "EUR",
  "Germany": "EUR",
  "India": "INR",
  "Indonesia": "IDR",
  "Italy": "EUR",
  "Japan": "JPY",
  "Mexico": "MXN",
  "Netherlands": "EUR",
  "Nigeria": "NGN",
  "Norway": "NOK",
  "Saudi Arabia": "SAR",
  "Singapore": "SGD",
  "South Africa": "ZAR",
  "South Korea": "KRW",
  "Spain": "EUR",
  "Switzerland": "CHF",
  "Thailand": "THB",
  "Turkey": "TRY",
  "United Arab Emirates": "AED",
  "United Kingdom": "GBP",
  "United States": "USD",
};

const COUNTRY_OPTIONS = Object.keys(COUNTRY_TO_CURRENCY);
const CURRENCY_OPTIONS = Array.from(new Set(Object.values(COUNTRY_TO_CURRENCY))).sort();

const COST_MODEL_STANDALONE_DRAFT_KEY = "spendiq_cost_model_draft_standalone";

type StandaloneRunRecord = {
  id: string;
  traceId: string;
  createdAt: string;
  product: string;
  country: string;
  currency: string;
  quantity: number | "";
  result: Record<string, any>;
  cId?: string;
  mId?: string;
};

type ProductSuggestion = {
  key: string;
  product: string;
  region: string;
};

const isInvalidJsonResponse = (result: any): boolean => {
  const foreachItems: any[] = result?.data?.output?.foreach ?? [];
  if (!Array.isArray(foreachItems) || foreachItems.length === 0) return false;

  const firstAnswer = foreachItems[0]?.answer;
  return typeof firstAnswer === "string" && firstAnswer.toLowerCase().includes("invalid json");
};

const toNumber = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sumByKey = (rows: any[] | undefined, key: string): number =>
  (rows ?? []).reduce((acc, row) => acc + toNumber(row?.[key]), 0);

const getFieldTotal = (item: any, field: string): number => {
  const answer = item?.answer ?? {};
  const totals = answer?.totals ?? item?.totals ?? {};

  // Prefer pre-computed totals when present.
  const totalValue = toNumber(totals?.[field]);
  if (totalValue > 0) return totalValue;

  // Fallback for payloads where only detailed rows are present.
  if (field === "material_cost") return sumByKey(answer?.material_cost, "cost");
  if (field === "labour_cost") {
    const salary = sumByKey(answer?.labour_cost, "salary");
    const benefits = sumByKey(answer?.labour_cost, "benefits");
    return salary + benefits;
  }
  if (field === "capital_cost") return sumByKey(answer?.capital_cost, "cost");
  if (field === "utility_cost") return sumByKey(answer?.utility_cost, "cost");

  return totalValue;
};

const sumField = (items: any[], field: string): number =>
  items.reduce((acc, item) => acc + getFieldTotal(item, field), 0);

const getResultCurrency = (run: StandaloneRunRecord): string => {
  const foreachItems: any[] = run?.result?.data?.output?.foreach ?? [];
  for (const item of foreachItems) {
    const candidate = String(item?.answer?.totals?.currency ?? "").trim();
    if (candidate) return candidate.toUpperCase();
  }

  const fallback = String(run?.currency ?? "").trim();
  return fallback ? fallback.toUpperCase() : "USD";
};

const getVersionLineageKey = (run: StandaloneRunRecord): string => {
  const trace = String(run?.traceId ?? run?.result?.trace_id ?? "").trim();
  if (trace) return `trace:${trace}`;

  const product = String(run?.product ?? "").trim().toLowerCase();
  const country = String(run?.country ?? "").trim().toLowerCase();
  const currency = String(run?.currency ?? "").trim().toLowerCase();
  return `fallback:${product}|${country}|${currency}`;
};

/** Maps a public.cost_model_analysis DB row (is_standalone = true) into the shape used by the saved models table. */
const mapDbRowToStandaloneRun = (row: Record<string, any>): StandaloneRunRecord => {
  const output = normalizeCostModelResult(row?.output_data ?? {});
  const input = row?.input_data ?? {};
  const foreachItems: any[] = output?.data?.output?.foreach ?? [];
  const heading = foreachItems[0]?.answer?.heading ?? {};

  return {
    id: String(row?.id ?? row?.request_id ?? ""),
    traceId: String(row?.request_id ?? output?.trace_id ?? ""),
    createdAt: row?.created_at ?? new Date().toISOString(),
    product: String(heading?.line_item_description ?? "Standalone Product"),
    country: String(heading?.supplier_region ?? "Standalone Region"),
    currency: String(foreachItems[0]?.answer?.totals?.currency ?? ""),
    quantity: "",
    result: output,
    cId: input?.cId,
    mId: input?.mId,
  };
};

const convertStandaloneValue = (value: any) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d.getTime()) && value.trim() !== "") {
      return d.toISOString();
    }
    return value;
  }
  return value;
};

export default function OnDemandCostAnalysisView() {
  const { navigate, setStandaloneAnalysisResult } = useExcelData();
  const { runCostModelAnalysis, loading } = useSimplAI();
  const [isAdvancedDriversExpanded, setIsAdvancedDriversExpanded] = useState(true);
  const [newCostModelValue, setNewCostModelValue] = useState("");
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [selectedSuggestionKey, setSelectedSuggestionKey] = useState("");
  const [driverQuantity, setDriverQuantity] = useState<number | "">(1);
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [selectedCurrency, setSelectedCurrency] = useState(COUNTRY_TO_CURRENCY["India"]);
  const [savedModelsSearch, setSavedModelsSearch] = useState("");
  const [standaloneRuns, setStandaloneRuns] = useState<StandaloneRunRecord[]>([]);
  const [latestCleanedRows, setLatestCleanedRows] = useState<Record<string, any>[]>([]);

  // SAVED MODELS list is sourced from public.cost_model_analysis (is_standalone = true), not localStorage.
  useEffect(() => {
    let isCancelled = false;

    fetchCostModelAnalysisList(true)
      .then((rows: Record<string, any>[]) => {
        if (isCancelled) return;
        const mapped = (Array.isArray(rows) ? rows : []).map(mapDbRowToStandaloneRun);
        setStandaloneRuns(mapped);
      })
      .catch((err) => {
        console.error("Failed to load standalone cost model analyses:", err);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const latestCleaning = await fetchLatestSpendCleaning();
        const cleanedData = latestCleaning?.cleaned_data;
        const parsedCleanedData =
          typeof cleanedData === "string" ? JSON.parse(cleanedData) : cleanedData;
        const rows = Array.isArray(parsedCleanedData?.rows)
          ? parsedCleanedData.rows
          : Array.isArray(parsedCleanedData)
            ? parsedCleanedData
            : [];

        if (!cancelled) setLatestCleanedRows(rows);
      } catch (error) {
        console.error("Failed to load latest cleaned spend data:", error);
        if (!cancelled) setLatestCleanedRows([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const productSearchPool = useMemo(() => {
    try {
      const collected = new Map<string, ProductSuggestion>();

      for (const row of latestCleanedRows) {
        const lineItemDescription = getRowFieldValue(row, [
          "Line Item Description",
          "Product",
          "Item Description",
        ]);
        const supplierRegion = getRowFieldValue(row, [
          "Supplier Region",
          "Supplier Country",
          "Country",
          "Region",
        ]) || "Unknown";

        if (!lineItemDescription) continue;

        const suggestionKey = `${lineItemDescription}__${supplierRegion}`;
        if (!collected.has(suggestionKey)) {
          collected.set(suggestionKey, {
            key: suggestionKey,
            product: lineItemDescription,
            region: supplierRegion,
          });
        }
      }

      return Array.from(collected.values());
    } catch {
      return [] as ProductSuggestion[];
    }
  }, [latestCleanedRows]);

  const productSuggestions = useMemo(() => {
    const query = productSearchQuery.trim();
    if (query.length < 3) return [] as ProductSuggestion[];

    const queryLower = query.toLowerCase();
    const startsWithMatches: ProductSuggestion[] = [];
    const includesMatches: ProductSuggestion[] = [];

    for (const candidate of productSearchPool) {
      const productText = candidate.product.toLowerCase();
      const regionText = candidate.region.toLowerCase();
      const combinedText = `${productText} ${regionText}`;

      if (productText.startsWith(queryLower) || regionText.startsWith(queryLower)) {
        startsWithMatches.push(candidate);
      } else if (combinedText.includes(queryLower)) {
        includesMatches.push(candidate);
      }
    }

    const merged = [...startsWithMatches, ...includesMatches].slice(0, 8);
    if (merged.length > 0) return merged;

    // Fallback: keep showing what the user is typing if no relevant matches exist.
    return [
      {
        key: `fallback__${query}`,
        product: query,
        region: "Custom input",
      },
    ];
  }, [productSearchPool, productSearchQuery]);

  const savedModels = useMemo<SavedModelRow[]>(() => {
    const versionByRunId = new Map<string, string>();
    const lineageCounter = new Map<string, number>();

    // Build version numbers in chronological order so first save is v1.
    [...standaloneRuns].reverse().forEach((run) => {
      const lineageKey = getVersionLineageKey(run);
      const nextVersion = (lineageCounter.get(lineageKey) || 0) + 1;
      lineageCounter.set(lineageKey, nextVersion);
      versionByRunId.set(run.id, `v${nextVersion}`);
    });

    return standaloneRuns.map((run) => {
      const foreachItems: any[] = run?.result?.data?.output?.foreach ?? [];
      const totalCost = sumField(foreachItems, "total_cost");
      const currency = getResultCurrency(run);

      return {
        id: run.id,
        product: run.product,
        cost: `${currency} ${totalCost.toFixed(1)}`,
        conf: "--",
        ver: versionByRunId.get(run.id) || "v1",
        status: "Ready",
        createdAt: run.createdAt,
      };
    });
  }, [standaloneRuns]);

  const filteredSavedModels = useMemo(() => {
    const term = savedModelsSearch.trim().toLowerCase();
    if (!term) return savedModels;

    return savedModels.filter((row) => {
      const haystack = `${row.product} ${row.cost} ${row.conf} ${row.ver} ${row.status} ${row.createdAt}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [savedModels, savedModelsSearch]);

  const createStandaloneCostModelJson = () => {
    const productName = newCostModelValue.trim();
    const inputRow: Record<string, any> = {
      Line_Item_Description: convertStandaloneValue(productName),
      Supplier_Region: convertStandaloneValue(selectedCountry),
      Qty: convertStandaloneValue(driverQuantity === "" ? "" : Number(driverQuantity)),
      Currency: convertStandaloneValue(selectedCurrency),
    };
    const finalJson = {
      data: {
        input_data: [inputRow],
      },
    };
    console.log("FINAL STANDALONE COST MODEL JSON:", finalJson);
    console.log("Standalone columns sent to SimplAI:", Object.keys(inputRow));

    return finalJson;
  };

  const handleStandaloneCostModelAnalysis = async () => {
    try {
      const productName = newCostModelValue.trim();

      if (!productName) {
        toast.error("Please enter/select a product name.");
        return;
      }

      // STEP 1: Create JSON payload
      const jsonData = createStandaloneCostModelJson();

      // STEP 2: Call SimplAI Cost Model Analysis API with standalone flag
      let conversationIds: { cId?: string; mId?: string } = {};
      const result = await runCostModelAnalysis(jsonData, {
        isStandalone: true,
        onPollComplete: async ({ cId, mId }: any) => {
          conversationIds = { cId, mId };
        },
      });

      // STEP 3: Validate result
      const valid = result?.data || result?.trace_id || result;

      if (!valid) {
        console.warn("Invalid SimplAI response:", result);
        toast.error("Invalid SimplAI response");
        return;
      }

      if (isInvalidJsonResponse(result)) {
        console.warn("Standalone run returned invalid JSON payload:", result);
        toast.error("Standalone run failed: backend returned invalid JSON.");
        return;
      }

      // STEP 4: Store result with standalone marker
      const finalResult = {
        ...normalizeCostModelResult(result),
        isStandalone: true,
      };

      const traceId = String(finalResult?.trace_id || "");
      const draftRecord: Omit<StandaloneRunRecord, "id"> = {
        traceId,
        createdAt: new Date().toISOString(),
        product: productName,
        country: selectedCountry,
        currency: selectedCurrency,
        quantity: driverQuantity,
        result: finalResult,
        cId: conversationIds.cId,
        mId: conversationIds.mId,
      };

      console.log("✅ STANDALONE COST MODEL RESULT:", finalResult);

      // Store only the latest standalone draft; this can be overwritten by subsequent runs.
      localStorage.setItem(COST_MODEL_STANDALONE_DRAFT_KEY, JSON.stringify(draftRecord));
      setStandaloneAnalysisResult(finalResult);

      navigate("cost-model-analysis-standalone");

      toast.success("Standalone Cost Model Analysis completed. Use Save version to persist it.");
    } catch (err: any) {
      console.error("Standalone Cost Model Analysis failed:", err);
      toast.error(err?.message || "Cost Model Analysis failed");
    }
  };

  return (
    <div className="cma-view">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <span>Sourcing</span>
        <span className="breadcrumb__sep" aria-hidden="true">·</span>
        <span className="breadcrumb__current">On-Demand Cost Analysis</span>
      </nav>

      <div className="page-header" style={{ marginBottom: 28 }}>
        <div className="page-header__left">
          <h2 className="page-header__title">On-Demand Cost Analysis</h2>
          <p className="page-header__description">
            Generate a cost model for any product on demand, or reopen a previously saved model.
          </p>
        </div>
      </div>

      <div className="cma-standalone-wrap">
        <div className="cma-standalone-card">
          <div className="cma-standalone-label">NEW COST MODEL</div>
          <div className="cma-standalone-form-row">
            <input
              type="text"
              className="cma-standalone-input"
              value={newCostModelValue}
              onChange={(event) => {
                const nextQuery = event.target.value;
                setNewCostModelValue(nextQuery);
                setProductSearchQuery(nextQuery);
                setSelectedSuggestionKey("");
              }}
              placeholder="Search product"
              aria-label="New cost model search"
            />
            <button
              type="button"
              className="cma-standalone-run-btn flex items-center"
              onClick={handleStandaloneCostModelAnalysis}
              disabled={loading || !newCostModelValue.trim()}
              title="Run Cost Model Analysis"
            >
              <RunIcon></RunIcon>
              {loading ? "Running..." : "Run"}
            </button>
          </div>

          {loading && (
            <div className="cma-standalone-loader" role="status" aria-live="polite" aria-label="Cost model analysis in progress">
              <div className="cma-standalone-loader-bars" aria-hidden="true">
                <span className="cma-standalone-loader-bar cma-standalone-loader-bar-lg" />
                <span className="cma-standalone-loader-bar cma-standalone-loader-bar-md" />
                <span className="cma-standalone-loader-bar cma-standalone-loader-bar-sm" />
              </div>
              <p className="cma-standalone-loader-text">
                Hang tight!
                <br />
                Your request is being processed and may take a moment.
              </p>
            </div>
          )}

          {productSuggestions.length > 0 && (
            <div className="cma-product-suggestions">
              <div className="cma-product-suggestions__title">Which product did you mean?</div>
              <div className="cma-product-suggestions__list" role="listbox" aria-label="Product suggestions">
                {productSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.key}
                    type="button"
                    className={`cma-product-suggestions__item${selectedSuggestionKey === suggestion.key ? " is-active" : ""}`}
                    onClick={() => {
                      setNewCostModelValue(suggestion.product);
                      setSelectedSuggestionKey(suggestion.key);
                    }}
                  >
                    <span className="cma-product-suggestions__infoWrapper">
                      <span className="cma-product-suggestions__product">{suggestion.product}</span>
                      <span className="cma-product-suggestions__region">{suggestion.region}</span>
                    </span>
                    <span className="cma-product-suggestions__status flex items-center">
                      {selectedSuggestionKey === suggestion.key && (
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.8337 3.25L4.87533 9.20833L2.16699 6.5" stroke="#E0044E" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {selectedSuggestionKey === suggestion.key ? "Selected" : "Select"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="cma-advanced-drivers">
            <button
              type="button"
              className="cma-advanced-drivers__toggle"
              onClick={() => setIsAdvancedDriversExpanded((prev) => !prev)}
              aria-expanded={isAdvancedDriversExpanded}
            >
              <ChevronDown
                size={14}
                className={`cma-advanced-drivers__chevron${isAdvancedDriversExpanded ? "" : " is-open"}`}
                aria-hidden="true"
              />
              <span>Advanced drivers - optional</span>
            </button>

            {isAdvancedDriversExpanded && (
              <div className="cma-advanced-drivers__row">
                <div className="cma-advanced-drivers__field">
                  <label htmlFor="advanced-driver-quantity" className="cma-advanced-drivers__label">
                    Quantity
                  </label>
                  <input
                    id="advanced-driver-quantity"
                    type="number"
                    min={0}
                    className="cma-advanced-drivers__input"
                    value={driverQuantity}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDriverQuantity(nextValue === "" ? "" : Number(nextValue));
                    }}
                    placeholder="Enter Quantity"
                  />
                </div>

                <div className="cma-advanced-drivers__field">
                  <label htmlFor="advanced-driver-country" className="cma-advanced-drivers__label">
                    Region
                  </label>
                  <select
                    id="advanced-driver-country"
                    className="cma-advanced-drivers__select"
                    value={selectedCountry}
                    onChange={(event) => {
                      const country = event.target.value;
                      setSelectedCountry(country);
                      setSelectedCurrency(COUNTRY_TO_CURRENCY[country] || "");
                    }}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cma-advanced-drivers__field">
                  <label htmlFor="advanced-driver-currency" className="cma-advanced-drivers__label">
                    Currency
                  </label>
                  <select
                    id="advanced-driver-currency"
                    className="cma-advanced-drivers__select"
                    value={selectedCurrency}
                    onChange={(event) => setSelectedCurrency(event.target.value)}
                  >
                    {CURRENCY_OPTIONS.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="cma-standalone-card">
          <div className="cma-standalone-saved-head">
            <div className="cma-standalone-saved-title">SAVED MODELS · {filteredSavedModels.length}</div>
            <input
              type="text"
              className="cma-standalone-search"
              placeholder="Search"
              value={savedModelsSearch}
              onChange={(event) => setSavedModelsSearch(event.target.value)}
              aria-label="Search saved models"
            />
          </div>

          <div className="cma-standalone-table-wrap">
            <table className="cma-standalone-table">
              <thead>
                <tr>
                  <th>PRODUCT</th>
                  <th>COST</th>
                  <th>CONF.</th>
                  <th>VER</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filteredSavedModels.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      const selectedRun = standaloneRuns.find((run) => run.id === row.id);
                      if (selectedRun?.result) {
                        setStandaloneAnalysisResult(selectedRun.result);
                        // Carry product/region metadata (sourced from DB) so the standalone view's summary card is accurate.
                        localStorage.setItem(COST_MODEL_STANDALONE_DRAFT_KEY, JSON.stringify(selectedRun));
                      }
                      navigate("cost-model-analysis-standalone");
                    }}
                    style={{ cursor: "pointer" }}
                    title="Open standalone model"
                  >
                    <td>{row.product}</td>
                    <td>{row.cost}</td>
                    <td>{row.conf}</td>
                    <td>{row.ver}</td>
                    <td>{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
